import { useEffect, useRef, useState } from "react";
import api, { rupee } from "../lib/api";
import { toast } from "sonner";
import { playSound } from "../lib/orderSounds";
import { getSeen, markSeen, markAck } from "../lib/orderAlertStore";
import { BellRing, Check } from "lucide-react";

// Mounted once inside AdminLayout. Rings CONTINUOUSLY while there are
// unacknowledged new orders and shows an Acknowledge banner. Clicking
// Acknowledge accepts the orders server-side (pending -> accepted) and stops the ring.
export function OrderAlertWatcher() {
  const cfg = useRef({ enabled: true, sound: "chime", custom: "" });
  const since = useRef(new Date().toISOString());
  const pendingRef = useRef([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const setPend = (list) => { pendingRef.current = list; setPending(list); };

  useEffect(() => {
    let pollId, cfgId, ringId, stopped = false;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const loadCfg = () => api.get("/admin/settings").then((r) => {
      const s = r.data || {};
      cfg.current = { enabled: s.order_alert_enabled !== false, sound: s.order_alert_sound || "chime", custom: s.order_alert_custom_sound_url || "" };
    }).catch(() => {});

    const notify = (o) => {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          const n = new Notification(`New Order ${o.order_number}`, { body: `${o.customer_name} · ₹${Math.round(o.total)}`, tag: o.id, requireInteraction: true, icon: "/favicon.ico" });
          n.onclick = () => { window.focus(); n.close(); };
        } catch (e) { /* ignore */ }
      }
    };

    const poll = async () => {
      const c = cfg.current;
      if (!c.enabled || stopped) return;
      try {
        const { data } = await api.get(`/admin/orders/alerts?since=${encodeURIComponent(since.current)}&limit=20`);
        if (data && data.length) {
          since.current = data[0].created_at; // newest first
          const seen = getSeen();
          const fresh = data.filter((o) => !seen.has(o.id) && !o.acknowledged_at);
          if (fresh.length) {
            markSeen(fresh.map((o) => o.id));
            fresh.forEach(notify);
            const ids = new Set(pendingRef.current.map((o) => o.id));
            setPend([...pendingRef.current, ...fresh.filter((o) => !ids.has(o.id))]);
          }
        }
      } catch (e) { /* ignore */ }
    };

    // Continuous ring — repeats every 4s until acknowledged; never stops on its own.
    ringId = setInterval(() => {
      const c = cfg.current;
      if (c.enabled && pendingRef.current.length > 0) playSound(c.sound, c.custom);
    }, 4000);

    loadCfg();
    poll();
    pollId = setInterval(poll, 15000);
    cfgId = setInterval(loadCfg, 60000);
    return () => { stopped = true; clearInterval(pollId); clearInterval(cfgId); clearInterval(ringId); };
  }, []);

  const acknowledgeAll = async () => {
    const list = [...pendingRef.current];
    setBusy(true);
    for (const o of list) {
      try { await api.post(`/admin/orders/${o.id}/acknowledge`); } catch (e) { /* ignore */ }
    }
    markAck(list.map((o) => o.id));
    setPend([]);
    setBusy(false);
    toast.success(`${list.length} order${list.length > 1 ? "s" : ""} acknowledged & accepted`);
  };

  if (pending.length === 0) return null;
  return (
    <div data-testid="order-ack-banner" className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md bg-navy text-cream rounded-2xl shadow-2xl border border-gold/40 p-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0 animate-pulse"><BellRing size={20} /></span>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg leading-tight">{pending.length} New Order{pending.length > 1 ? "s" : ""}!</div>
          <div className="text-xs text-cream/70 mt-0.5 truncate" data-testid="ack-banner-details">
            {pending.slice(0, 2).map((o) => `${o.order_number} · ${o.customer_name} · ${rupee(o.total)}`).join("   |   ")}
            {pending.length > 2 ? `  +${pending.length - 2} more` : ""}
          </div>
        </div>
      </div>
      <button onClick={acknowledgeAll} disabled={busy} data-testid="order-ack-btn"
        className="w-full mt-3 bg-gold text-navy font-semibold py-2.5 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2 active:scale-95 disabled:opacity-60">
        <Check size={16} /> {busy ? "Accepting..." : "Acknowledge & Accept"}
      </button>
    </div>
  );
}
